import axios from 'axios';
import { API_BASE_URL } from './apiConfig';

class HSCodesApi {
  constructor() {
    this.baseURL = `${API_BASE_URL}/api/hs-codes`;
  }

  /**
   * Get HS codes with optional filtering
   * @param {Object} filters - Filter options
   * @param {string} filters.mineral - Filter by mineral name
   * @param {string} filters.category - Filter by category
   * @param {string} filters.hs_code - Filter by HS code
   * @param {number} filters.limit - Maximum number of results
   * @param {number} filters.offset - Number of results to skip
   * @returns {Promise<Object>} HS codes data with pagination
   */
  async getHSCodes(filters = {}) {
    try {
      const params = new URLSearchParams();
      
      if (filters.mineral) params.append('mineral', filters.mineral);
      if (filters.category) params.append('category', filters.category);
      if (filters.hs_code) params.append('hs_code', filters.hs_code);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.offset) params.append('offset', filters.offset);

      const response = await axios.get(`${this.baseURL}?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching HS codes:', error);
      throw new Error(error.response?.data?.detail || 'Failed to fetch HS codes');
    }
  }

  /**
   * Get all available minerals for filter dropdown
   * @returns {Promise<Array>} List of unique minerals
   */
  async getMinerals() {
    try {
      const response = await axios.get(`${this.baseURL}?limit=1`);
      return response.data.filters?.minerals || [];
    } catch (error) {
      console.error('Error fetching minerals:', error);
      throw new Error('Failed to fetch minerals list');
    }
  }

  /**
   * Get all available categories for filter dropdown
   * @returns {Promise<Array>} List of unique categories
   */
  async getCategories() {
    try {
      const response = await axios.get(`${this.baseURL}?limit=1`);
      return response.data.filters?.categories || [];
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw new Error('Failed to fetch categories list');
    }
  }

  /**
   * Search HS codes by text query
   * @param {string} query - Search query
   * @param {Object} additionalFilters - Additional filter options
   * @returns {Promise<Object>} Filtered HS codes data
   */
  async searchHSCodes(query, additionalFilters = {}) {
    try {
      const filters = {
        ...additionalFilters,
        hs_code: query, // Search in HS codes
        limit: 50 // Limit search results
      };
      
      return await this.getHSCodes(filters);
    } catch (error) {
      console.error('Error searching HS codes:', error);
      throw new Error('Failed to search HS codes');
    }
  }
}

const hsCodesApi = new HSCodesApi();

export default hsCodesApi;
